import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileCheck, CheckCircle, MapPin, Clock, ArrowRight, Phone, AlertCircle, Car, Calendar, CreditCard, DollarSign, FileText, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Alert, AlertDescription } from '../components/ui/alert';

const notaryFees = [
  { service: 'Notarization (per signature/stamp)', price: '$10' },
  { service: 'Acknowledgments', price: '$10' },
  { service: 'Jurats', price: '$10' },
  { service: 'Oaths & Affirmations', price: '$10' },
];

const travelFees = [
  { distance: '0-5 miles (Statesboro)', price: '$15' },
  { distance: '6-15 miles (Bulloch County)', price: '$20' },
  { distance: '16-30 miles (Surrounding areas)', price: '$30' },
  { distance: '30+ miles', price: '$40+' },
];

const loanSigningFees = [
  { service: 'Buyer / Seller Packages', price: '$75–$125' },
  { service: 'Refinance / HELOC', price: '$100–$150' },
  { service: 'Cash Purchase', price: '$85–$120' },
];

const commonServices = [
  'Power of Attorney', 'Affidavits', 'Deeds & Contracts',
  'School & Medical Forms', 'Business Documents', 'DMV / Vehicle Title Transfers'
];

const whatToBring = [
  'Valid government-issued photo ID',
  'All signers must be present',
  'Documents must be unsigned',
  'Know what you are signing',
];

export default function MobileNotary() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-purple-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#4f62c8] to-[#2a2873] text-white text-sm font-medium mb-6">
              <FileCheck className="w-4 h-4" />
              <span>Georgia Mobile Notary</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#2a2873] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="mobile-notary-title">
              Mobile Notary Services
            </h1>
            <p className="text-lg text-[#5a2b8a]/80 mb-4">Statesboro, GA & Surrounding Areas</p>
            <p className="text-base text-[#5a2b8a]/70 mb-8 leading-relaxed">
              Professional mobile notary services throughout Georgia. We come to your home, office, hospital, or any convenient location.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/book-now">
                <Button size="lg" className="rounded-full bg-gradient-to-r from-[#4f62c8] to-[#2a2873] hover:from-[#3f52b8] hover:to-[#1a1853] px-8 py-6 text-lg shadow-xl" data-testid="book-notary-btn">
                  Book a Notary <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:912-388-6305">
                <Button size="lg" variant="outline" className="rounded-full border-2 border-[#4f62c8] text-[#4f62c8] hover:bg-[#4f62c8]/10 px-8 py-6 text-lg" data-testid="call-notary-btn">
                  <Phone className="mr-2 w-5 h-5" /> 912-388-6305
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Notary Fees & Travel Fees */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Notary Fees */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Card className="border-0 shadow-xl h-full" data-testid="notary-fees-card">
                <CardHeader className="bg-gradient-to-r from-[#c31c31] to-[#e05b6d] text-white rounded-t-lg">
                  <CardTitle className="flex items-center gap-3 text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <DollarSign className="w-6 h-6" />
                    Notary Fees (Set by Georgia Law)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <Table>
                    <TableBody>
                      {notaryFees.map((item, index) => (
                        <TableRow key={index} className="hover:bg-red-50">
                          <TableCell className="font-medium text-[#2a2873]">{item.service}</TableCell>
                          <TableCell className="text-right text-[#c31c31] font-bold text-lg">{item.price}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </motion.div>

            {/* Travel Fees */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <Card className="border-0 shadow-xl h-full" data-testid="travel-fees-card">
                <CardHeader className="bg-gradient-to-r from-[#4f62c8] to-[#2a2873] text-white rounded-t-lg">
                  <CardTitle className="flex items-center gap-3 text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Car className="w-6 h-6" />
                    Mobile Travel Fees
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <Table>
                    <TableBody>
                      {travelFees.map((item, index) => (
                        <TableRow key={index} className="hover:bg-blue-50">
                          <TableCell className="font-medium text-[#2a2873]">{item.distance}</TableCell>
                          <TableCell className="text-right text-[#4f62c8] font-bold text-lg">{item.price}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Loan Signing & Common Services */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Loan Signing */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Card className="border-0 shadow-xl h-full" data-testid="loan-signing-card">
                <CardHeader className="bg-gradient-to-r from-[#5a2b8a] to-[#8c53b0] text-white rounded-t-lg">
                  <CardTitle className="flex items-center gap-3 text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Home className="w-6 h-6" />
                    Loan Signing Agent Services
                  </CardTitle>
                  <p className="text-white/80 text-sm mt-1">Flat-rate professional services</p>
                </CardHeader>
                <CardContent className="pt-6">
                  <Table>
                    <TableBody>
                      {loanSigningFees.map((item, index) => (
                        <TableRow key={index} className="hover:bg-purple-50">
                          <TableCell className="font-medium text-[#2a2873]">{item.service}</TableCell>
                          <TableCell className="text-right text-[#5a2b8a] font-bold text-lg">{item.price}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </motion.div>

            {/* Common Services */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <Card className="border-0 shadow-xl h-full" data-testid="common-services-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-xl text-[#2a2873]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <FileText className="w-6 h-6 text-[#c31c31]" />
                    Common Mobile Notary Services
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {commonServices.map((service, index) => (
                      <div key={index} className="flex items-center gap-2 p-3 rounded-lg bg-gradient-to-r from-red-50 to-purple-50">
                        <CheckCircle className="w-5 h-5 text-[#c31c31]" />
                        <span className="text-[#2a2873] font-medium">{service}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* After Hours & What to Bring */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* After Hours */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Card className="border-0 shadow-xl" data-testid="after-hours-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-xl text-[#2a2873]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Clock className="w-6 h-6 text-[#5a2b8a]" />
                    After-Hours & Rush Services
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center p-4 rounded-lg bg-purple-50 border-l-4 border-[#5a2b8a]">
                    <span className="font-medium text-[#2a2873]">Evenings, Weekends, Holidays</span>
                    <span className="font-bold text-[#5a2b8a]">+$25–$50</span>
                  </div>
                  <div className="flex justify-between items-center p-4 rounded-lg bg-red-50 border-l-4 border-[#c31c31]">
                    <span className="font-medium text-[#2a2873]">Same-Day / Urgent Requests</span>
                    <span className="font-bold text-[#c31c31]">+$25</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* What to Bring */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <Card className="border-0 shadow-xl" data-testid="what-to-bring-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-xl text-[#2a2873]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <CheckCircle className="w-6 h-6 text-[#c31c31]" />
                    What to Bring
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {whatToBring.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#c31c31] to-[#5a2b8a] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-[#5a2b8a]">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Area & Payment */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Service Area - Georgia */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Card className="border-0 shadow-xl" data-testid="service-area-card">
                <CardHeader className="bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] text-white rounded-t-lg">
                  <CardTitle className="flex items-center gap-3 text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <MapPin className="w-6 h-6" />
                    Georgia Service Area
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-[#5a2b8a]/80 mb-4">
                    <strong className="text-[#2a2873]">Statesboro, Bulloch County & surrounding Georgia areas</strong>
                  </p>
                  <p className="text-[#5a2b8a]/70 mb-4">
                    Mobile service available to homes, offices, hospitals, churches, and public locations.
                  </p>
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-gradient-to-r from-red-50 to-purple-50 border-l-4 border-[#c31c31]">
                    <Phone className="w-5 h-5 text-[#c31c31]" />
                    <div>
                      <p className="font-bold text-[#2a2873]">912-388-6305</p>
                      <p className="text-sm text-[#5a2b8a]/70">Hope N. - LifeBreath Mobile Notary</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Service Area - Jacksonville, FL */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <Card className="border-0 shadow-xl" data-testid="jacksonville-service-card">
                <CardHeader className="bg-gradient-to-r from-[#4f62c8] to-[#2a2873] text-white rounded-t-lg">
                  <CardTitle className="flex items-center gap-3 text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <MapPin className="w-6 h-6" />
                    Jacksonville, Florida
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-[#5a2b8a]/80 mb-4">
                    <strong className="text-[#2a2873]">Serving Jacksonville & surrounding areas</strong>
                  </p>
                  <p className="text-[#5a2b8a]/70 mb-4">
                    Convenient mobile notary services traveling to homes, offices, hospitals, nursing facilities, and other agreed locations. Flexible scheduling available.
                  </p>
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 border-l-4 border-[#4f62c8]">
                    <Phone className="w-5 h-5 text-[#4f62c8]" />
                    <div>
                      <p className="font-bold text-[#2a2873]">904-465-5065</p>
                      <p className="text-sm text-[#5a2b8a]/70">Shiffon Champion-Holmes</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Payment Methods */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-8">
            <Card className="border-0 shadow-xl" data-testid="payment-methods-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-xl text-[#2a2873]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <CreditCard className="w-6 h-6 text-[#c31c31]" />
                  Accepted Payment Methods
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 flex-1 min-w-[200px]">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-[#2a2873]">Cash</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 flex-1 min-w-[200px]">
                    <CreditCard className="w-5 h-5 text-[#4f62c8]" />
                    <span className="font-medium text-[#2a2873]">Square (invoice or QR code)</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Disclaimer */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-8">
            <Alert className="bg-amber-50 border-amber-300" data-testid="legal-disclaimer">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Important:</strong> Notary fees are regulated by Georgia law. Travel and convenience fees are quoted and agreed upon prior to service. I am not an attorney and cannot provide legal advice.
              </AlertDescription>
            </Alert>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-[#4f62c8] via-[#5a2b8a] to-[#2a2873]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Need a Notary?</h2>
            <p className="text-white/90 mb-8 max-w-2xl mx-auto">Call or text to schedule your mobile notary appointment today.</p>
            
            <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto mb-8">
              <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
                <p className="text-white/70 text-sm mb-2">Georgia (Statesboro Area)</p>
                <a href="tel:912-388-6305" className="text-2xl font-bold text-white hover:underline flex items-center justify-center gap-2">
                  <Phone className="w-5 h-5" /> 912-388-6305
                </a>
                <p className="text-white/60 text-sm mt-1">Hope N.</p>
              </div>
              <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
                <p className="text-white/70 text-sm mb-2">Florida (Jacksonville Area)</p>
                <a href="tel:904-465-5065" className="text-2xl font-bold text-white hover:underline flex items-center justify-center gap-2">
                  <Phone className="w-5 h-5" /> 904-465-5065
                </a>
                <p className="text-white/60 text-sm mt-1">Shiffon Champion-Holmes</p>
              </div>
            </div>
            
            <Link to="/book-now">
              <Button size="lg" className="rounded-full bg-[#c31c31] text-white hover:bg-[#a51828] px-8 py-6 text-lg shadow-xl hover:scale-105 transition-all" data-testid="cta-notary-book-btn">
                Book Online <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
